import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MetricCard from './MetricCard';
import AISummarySection from './AISummarySection';

const OverviewTab = ({ analysisData, onSave }) => {
    const [businessOverview, setBusinessOverview] = useState(analysisData?.business_overview || '');
    const [businessTagline, setBusinessTagline] = useState(analysisData?.business_tagline || '');
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        setBusinessOverview(analysisData?.business_overview || '');
        setBusinessTagline(analysisData?.business_tagline || '');
    }, [analysisData]);

    const scanDuration = analysisData.scanStartedAt && analysisData.scanFinishedAt
        ? ((new Date(analysisData.scanFinishedAt) - new Date(analysisData.scanStartedAt)) / 1000).toFixed(1)
        : null;

    // Extract top 5 colors from all available sources
    const getTop5Colors = () => {
        const colors = analysisData.themeColors || {};
        const colorSet = new Set();

        // Add individual color properties (in priority order)
        if (colors.primary) colorSet.add(colors.primary);
        if (colors.secondary) colorSet.add(colors.secondary);
        if (colors.accent) colorSet.add(colors.accent);
        if (colors.background) colorSet.add(colors.background);
        if (colors.text) colorSet.add(colors.text);
        if (colors.metaTheme) colorSet.add(colors.metaTheme);

        // Add from allBackgrounds (filter valid hex colors only)
        if (Array.isArray(colors.allBackgrounds)) {
            colors.allBackgrounds.forEach(color => {
                if (color && /^#[0-9a-fA-F]{3,6}$/i.test(color)) {
                    colorSet.add(color);
                }
            });
        }

        // Add from allTextColors (filter valid hex colors only)
        if (Array.isArray(colors.allTextColors)) {
            colors.allTextColors.forEach(color => {
                if (color && /^#[0-9a-fA-F]{3,6}$/i.test(color)) {
                    colorSet.add(color);
                }
            });
        }

        // Add from allBorderColors (filter valid hex colors only)
        if (Array.isArray(colors.allBorderColors)) {
            colors.allBorderColors.forEach(color => {
                if (color && /^#[0-9a-fA-F]{3,6}$/i.test(color)) {
                    colorSet.add(color);
                }
            });
        }

        // Convert set to array and return first 5
        return Array.from(colorSet).slice(0, 5);
    };

    const top5Colors = getTop5Colors();

    const handleSaveEdits = () => {
        if (onSave) {
            onSave({
                business_overview: businessOverview,
                business_tagline: businessTagline
            });
        }
        setIsEditing(false);
    };

    console.log("data: ", analysisData)
    return (
        <div className="tab-content">
            {/* AI Summary Section */}
            <AISummarySection analysisData={analysisData} />

            {/* Logo & URL Header */}
            {analysisData.logo?.url && (
                <div className="overview-logo-banner">
                    <img src={analysisData.logo.url} alt="Site Logo" className="overview-logo-img" />
                    <div className="overview-url-info">
                        <a href={analysisData.originalUrl} target="_blank" rel="noopener noreferrer">
                            {analysisData.originalUrl}
                        </a>
                        {scanDuration && <span className="scan-time-badge">Scanned in {scanDuration}s</span>}
                    </div>
                </div>
            )}

            {/* Business Information Section */}
            <div className="business-info-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3>Business Information</h3>
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: isEditing ? '#ef4444' : '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                </div>

                {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label htmlFor="business-tagline" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '13px' }}>Business Tagline</label>
                            <input
                                id="business-tagline"
                                type="text"
                                value={businessTagline}
                                onChange={(e) => setBusinessTagline(e.target.value)}
                                placeholder="Enter business tagline..."
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    transition: 'all 0.2s ease'
                                }}
                            />
                        </div>
                        <div>
                            <label htmlFor="business-overview" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '13px' }}>Business Overview</label>
                            <textarea
                                id="business-overview"
                                value={businessOverview}
                                onChange={(e) => setBusinessOverview(e.target.value)}
                                placeholder="Enter business overview..."
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    minHeight: '100px',
                                    fontFamily: 'inherit',
                                    resize: 'vertical',
                                    transition: 'all 0.2s ease'
                                }}
                            />
                        </div>
                        <button
                            onClick={handleSaveEdits}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Save Changes
                        </button>
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: '1rem' }}>
                            <p style={{ margin: '0 0 0.3rem 0', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Tagline</p>
                            <p style={{ margin: '0', fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>
                                {businessTagline || 'No tagline added yet'}
                            </p>
                        </div>
                        <div>
                            <p style={{ margin: '0 0 0.3rem 0', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Overview</p>
                            <p style={{ margin: '0', fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                                {businessOverview || 'No overview added yet'}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Brand Colors Palette Section - Top 5 Colors */}
            <div className="metric-grid" style={{ marginBottom: '2rem' }}>
                <MetricCard title="Brand Colors (Top 5)">
                    {top5Colors.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {top5Colors.map((color, idx) => (
                                <div key={`color-${idx}-${color}`} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <button
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            backgroundColor: color,
                                            borderRadius: '6px',
                                            border: '2px solid #e2e8f0',
                                            flexShrink: 0,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            padding: '0',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                        }}
                                        title={`${color} - Click to copy`}
                                        onClick={() => navigator.clipboard.writeText(color)}
                                        onMouseEnter={(e) => {
                                            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                            e.target.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                            e.target.style.transform = 'scale(1)';
                                        }}
                                        aria-label={`Copy ${color} to clipboard`}
                                    ></button>
                                    <div>
                                        <p style={{ margin: '0 0 0.25rem 0', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                                            Color {idx + 1}
                                        </p>
                                        <p style={{ margin: '0', fontSize: '14px', fontWeight: '600', fontFamily: 'monospace' }}>
                                            {color}
                                        </p>
                                        <p style={{ margin: '0.25rem 0 0 0', fontSize: '11px', color: '#94a3b8' }}>
                                            Click to copy
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0' }}>No colors detected</p>
                    )}
                </MetricCard>

                <MetricCard title="Content Summary">
                    <p><strong>Product Images:</strong> {analysisData.productImages?.total || 0}</p>
                    <p><strong>With Alt Text:</strong> {(analysisData.productImages?.images || []).filter(img => img.alt).length}</p>
                    <p><strong>Without Alt Text:</strong> {(analysisData.productImages?.images || []).filter(img => !img.alt).length}</p>
                    <p style={{ fontSize: '12px', color: '#64748b', marginTop: '0.75rem' }}>
                        Total images detected across the website
                    </p>
                </MetricCard>
            </div>

            {/* Basic Info */}
            <div className="metric-grid">
                <MetricCard title="Website Metadata">
                    <div>
                        <p style={{ margin: '0 0 0.5rem 0' }}><strong>Title:</strong></p>
                        <p style={{ margin: '0 0 1rem 0', fontSize: '13px', color: '#475569' }}>{analysisData.basicMeta?.title || 'Not set'}</p>
                        <p style={{ margin: '0 0 0.5rem 0' }}><strong>Description:</strong></p>
                        <p style={{ margin: '0', fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>{analysisData.basicMeta?.description || 'Not set'}</p>
                    </div>
                </MetricCard>
            </div>
        </div>
    );
};

OverviewTab.propTypes = {
    analysisData: PropTypes.object.isRequired,
    onSave: PropTypes.func
};

export default OverviewTab;
